// Basit backend API istemcisi — token'ları localStorage'da tutar, otomatik refresh yapar.

// Boş string ("") = aynı origin (production: Nginx /api'yi backend'e proxy'ler).
const API_URL = process.env.API_URL != null ? process.env.API_URL : 'http://localhost:3001';
const ACCESS_KEY = 'vokelle_access';
const REFRESH_KEY = 'vokelle_refresh';

export function getAccessToken() {
  return localStorage.getItem(ACCESS_KEY);
}
export function setTokens({ accessToken, refreshToken }) {
  if (accessToken) localStorage.setItem(ACCESS_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
}
export function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

async function rawRequest(method, path, body, token) {
  const res = await fetch(API_URL + '/api' + path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body != null ? JSON.stringify(body) : undefined,
  });
  let json = null;
  try {
    json = await res.json();
  } catch (_) {}
  return { status: res.status, ok: res.ok, json };
}

async function tryRefresh() {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return false;
  const res = await rawRequest('POST', '/auth/refresh', { refreshToken });
  if (res.ok && res.json?.accessToken) {
    setTokens(res.json);
    return true;
  }
  clearTokens();
  return false;
}

export async function apiRequest(method, path, body, { auth = true } = {}) {
  let token = auth ? getAccessToken() : null;
  let res = await rawRequest(method, path, body, token);

  if (auth && res.status === 401 && (await tryRefresh())) {
    res = await rawRequest(method, path, body, getAccessToken());
  }

  if (!res.ok) {
    const err = new Error(res.json?.error?.message || 'request_failed');
    err.code = res.json?.error?.code || 'request_failed';
    err.status = res.status;
    throw err;
  }
  return res.json;
}

export const api = {
  register: (data) => apiRequest('POST', '/auth/register', data, { auth: false }),
  login: (data) => apiRequest('POST', '/auth/login', data, { auth: false }),
  me: () => apiRequest('GET', '/auth/me'),
  health: () => apiRequest('GET', '/health', null, { auth: false }),

  // Dashboard (Faz 2) — opsiyonel `server` = seçili sunucu lisans anahtarı
  summary: (server) => apiRequest('GET', `/dashboard/summary${server ? `?server=${server}` : ''}`),
  overview: (server) => apiRequest('GET', `/dashboard/overview${server ? `?server=${server}` : ''}`),
  metrics: (range = '7d', server) =>
    apiRequest('GET', `/dashboard/metrics?range=${encodeURIComponent(range)}${server ? `&server=${server}` : ''}`),
  alerts: (limit = 50, server) => apiRequest('GET', `/dashboard/alerts?limit=${limit}${server ? `&server=${server}` : ''}`),
  license: (server) => apiRequest('GET', `/dashboard/license${server ? `?server=${server}` : ''}`),

  // Çoklu sunucu (Faz 9)
  servers: () => apiRequest('GET', '/dashboard/servers'),
  renameServer: (key, label) => apiRequest('POST', `/dashboard/servers/${encodeURIComponent(key)}/label`, { label }),
  addServerQuote: (count) => apiRequest('GET', `/payment/add-server/quote?count=${count}`),
  addServer: (count) => apiRequest('POST', '/payment/add-server', { count }),

  // Bildirim kanalları (Faz 3)
  listChannels: () => apiRequest('GET', '/dashboard/notifications'),
  createChannel: (data) => apiRequest('POST', '/dashboard/notifications', data),
  updateChannel: (id, data) => apiRequest('PATCH', `/dashboard/notifications/${id}`, data),
  deleteChannel: (id) => apiRequest('DELETE', `/dashboard/notifications/${id}`),
  testChannel: (id) => apiRequest('POST', `/dashboard/notifications/${id}/test`),

  // Ödeme + lisans (Faz 4)
  validateCoupon: (code) => apiRequest('POST', '/payment/coupon/validate', { code }),
  checkout: (data) => apiRequest('POST', '/payment/checkout', data),
  confirmPayment: (merchantOid) => apiRequest('POST', '/payment/confirm', { merchantOid }),
  listCards: () => apiRequest('GET', '/payment/cards'),
  deleteCard: (ctoken) => apiRequest('DELETE', `/payment/cards/${encodeURIComponent(ctoken)}`),
  renewNow: () => apiRequest('POST', '/payment/renew-now'),
  billing: () => apiRequest('GET', '/dashboard/billing'),
  pauseSubscription: () => apiRequest('POST', '/dashboard/subscription/pause'),
  resumeSubscription: () => apiRequest('POST', '/dashboard/subscription/resume'),
  migrateLicense: (newServerLabel, server) => apiRequest('POST', '/dashboard/license/migrate', { newServerLabel, server }),
  setAutoRenew: (autoRenew) => apiRequest('PATCH', '/dashboard/subscription', { autoRenew }),
  downloadInfo: () => apiRequest('GET', '/dashboard/license/download/info'),
  getSchemaConfirmation: (server) => apiRequest('GET', `/dashboard/schema-confirmation${server ? `?server=${server}` : ''}`),
  submitSchemaConfirmation: (mapping, server) => apiRequest('POST', '/dashboard/schema-confirmation', { mapping, server }),

  // Ayarlar (dinamik fiyatlandırma)
  verifyEmail: (code) => apiRequest('POST', '/auth/verify-email', { code }),
  resendVerification: () => apiRequest('POST', '/auth/resend-verification'),

  publicSettings: () => apiRequest('GET', '/settings/public', null, { auth: false }),
  adminSettings: () => apiRequest('GET', '/admin/settings'),
  updateAdminSettings: (data) => apiRequest('PATCH', '/admin/settings', data),

  // Referral (Faz 6)
  referral: () => apiRequest('GET', '/dashboard/referral'),

  // Büyüme / Reklam Atıf (Faz 1)
  growth: (server) => apiRequest('GET', `/dashboard/growth${server ? `?server=${server}` : ''}`),
  createCampaign: (data, server) =>
    apiRequest('POST', `/dashboard/campaigns${server ? `?server=${server}` : ''}`, data),
  updateCampaign: (id, data) => apiRequest('PATCH', `/dashboard/campaigns/${id}`, data),
  deleteCampaign: (id) => apiRequest('DELETE', `/dashboard/campaigns/${id}`),
  updateGrowthSettings: (data, server) =>
    apiRequest('PATCH', `/dashboard/growth/settings${server ? `?server=${server}` : ''}`, data),
  publisherIndex: () => apiRequest('GET', '/dashboard/publisher-index'),
  publisherHints: () => apiRequest('GET', '/dashboard/publisher-hints'),

  // Admin (Faz 6)
  adminCustomers: () => apiRequest('GET', '/admin/customers'),
  adminCustomer: (id) => apiRequest('GET', `/admin/customers/${id}`),
  adminSilentAgents: (days = 3) => apiRequest('GET', `/admin/silent-agents?days=${days}`),
  adminPublishers: () => apiRequest('GET', '/admin/publishers'),
  adminRevenue: () => apiRequest('GET', '/admin/revenue-summary'),
  adminCoupons: () => apiRequest('GET', '/admin/coupons'),
  adminCreateCoupon: (data) => apiRequest('POST', '/admin/coupons', data),
  adminUpdateCoupon: (id, data) => apiRequest('PATCH', `/admin/coupons/${id}`, data),
  adminDeleteCoupon: (id) => apiRequest('DELETE', `/admin/coupons/${id}`),
  adminGrantDiscount: (id, data) => apiRequest('POST', `/admin/customers/${id}/grant-discount`, data),
  adminGrantFreeTime: (id, data) => apiRequest('POST', `/admin/customers/${id}/grant-free-time`, data),
  adminSetSlots: (id, serverSlots) => apiRequest('POST', `/admin/customers/${id}/set-slots`, { serverSlots }),

  // Admin — izleme (operatör): tüm müşterilerin verisi
  adminMonitoringOverview: () => apiRequest('GET', '/admin/monitoring/overview'),
  adminAllAlerts: (params = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== '')).toString();
    return apiRequest('GET', `/admin/monitoring/alerts${q ? `?${q}` : ''}`);
  },
  adminAllAgents: () => apiRequest('GET', '/admin/monitoring/agents'),
  adminCampaigns: () => apiRequest('GET', '/admin/monitoring/campaigns'),
  adminCustomerMonitoring: (id, range = '7d', server) => apiRequest('GET', `/admin/customers/${id}/monitoring?range=${range}` + (server ? `&server=${server}` : '')),
};

// Ajan dosyasını auth header ile indirip tarayıcı indirmesini tetikler.
export async function downloadAgentFile(platform) {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}/api/dashboard/license/download?platform=${platform}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let msg = 'download_failed';
    try { msg = (await res.json()).error?.message || msg; } catch (_) {}
    const e = new Error(msg);
    e.status = res.status;
    throw e;
  }
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const m = cd.match(/filename="?([^"]+)"?/);
  const name = m ? m[1] : `vokelle-agent-${platform}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return name;
}

export { API_URL };
